import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, ImageIcon, CheckCircle2, XCircle, ArrowRightCircle, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PRIORITY_STYLES, STATUS_STYLES, timeAgo } from "../components/shared";

export default function Reports() {
  const { effectiveRole } = useAuth();
  const canTriage = ["client_admin", "manager"].includes(effectiveRole);
  const [reports, setReports] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("view"); // view | close | toAction
  const [note, setNote] = useState("");
  const [resolution, setResolution] = useState("completed");
  const [aForm, setAForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reports").then((r) => setReports(r.data));
  useEffect(() => { load(); }, []);

  const openReport = (r) => { setSelected(r); setMode("view"); setNote(""); };

  const doClose = async (res) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${selected.id}/close`, { resolution: res, note });
      toast.success("Report closed");
      setSelected(data);
      setReports((prev) => prev.map((x) => (x.id === data.id ? data : x)));
      setMode("view");
    } catch (e) { toast.error("Failed to close report"); } finally { setBusy(false); }
  };

  const startAction = () => {
    setAForm({
      title: selected.title, description: selected.description || "",
      priority: selected.priority || "medium", status: "open",
      due_date: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
    });
    setMode("toAction");
  };

  const submitAction = async () => {
    if (!aForm.title) return toast.error("Title is required");
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${selected.id}/action`, {
        title: aForm.title, description: aForm.description, priority: aForm.priority,
        status: aForm.status, due_date: aForm.due_date ? new Date(aForm.due_date).toISOString() : null,
      });
      toast.success("Action created and added to the board");
      setSelected(data.report);
      setReports((prev) => prev.map((x) => (x.id === data.report.id ? data.report : x)));
      setMode("view");
    } catch (e) { toast.error("Failed to create action"); } finally { setBusy(false); }
  };

  if (!reports) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  const filtered = reports.filter((r) => {
    const matchQ = !q || r.title.toLowerCase().includes(q.toLowerCase()) || r.ref.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || r.priority === filter;
    return matchQ && matchF;
  });

  const done = selected && (selected.status === "closed" || selected.status === "actioned");

  return (
    <div>
      <PageHeader title="Reports" subtitle="Review incoming reports and triage each one to closure or an action." />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or reference…" className="pl-9" data-testid="reports-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-44" data-testid="reports-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-hidden rounded-2xl border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-medium">Reference</th>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="hidden px-4 py-3 font-medium md:table-cell">Category</th>
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="hidden px-4 py-3 font-medium lg:table-cell">Reported</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} onClick={() => openReport(r)} data-testid={`report-row-${r.ref}`}
                  className="cursor-pointer border-b border-border/60 transition-colors hover:bg-secondary/50">
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{r.ref}</td>
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="hidden px-4 py-3 text-muted-foreground md:table-cell">{r.category}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={`rounded-full border ${PRIORITY_STYLES[r.priority]}`}>{r.priority}</Badge></td>
                  <td className="px-4 py-3"><Badge className={`rounded-full ${STATUS_STYLES[r.status]}`}>{r.status.replace("_", " ")}</Badge></td>
                  <td className="hidden px-4 py-3 text-muted-foreground lg:table-cell">{timeAgo(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">No reports found.</div>}
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`rounded-full border ${PRIORITY_STYLES[selected.priority]}`}>{selected.priority}</Badge>
                  <span className="font-mono text-xs text-muted-foreground">{selected.ref}</span>
                  <Badge className={`rounded-full ${STATUS_STYLES[selected.status]}`}>{selected.status.replace("_", " ")}</Badge>
                </div>
                <DialogTitle className="pt-1 text-left font-heading">{selected.title}</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {selected.photo ? (
                  <img src={selected.photo} alt="report" className="max-h-52 w-full rounded-xl border border-border object-cover" />
                ) : (
                  <div className="flex h-24 items-center justify-center rounded-xl bg-secondary text-muted-foreground"><ImageIcon className="h-6 w-6" /></div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Meta label="Category" value={selected.category} />
                  <Meta label="Reporter" value={selected.reporter_name} />
                  <Meta label="Assigned to" value={selected.assigned_to} />
                  <Meta label="Location" value={selected.location || "—"} />
                </div>
                {selected.description && <p className="text-sm text-muted-foreground">{selected.description}</p>}

                {/* Outcome banner */}
                {selected.status === "closed" && (
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm">
                    <div className="font-medium text-emerald-600 dark:text-emerald-400">
                      Closed — {selected.resolution === "no_action" ? "No action required" : "Completed"}
                    </div>
                    {selected.resolution_note && <div className="mt-1 text-muted-foreground">{selected.resolution_note}</div>}
                    {selected.closed_by && <div className="mt-1 text-xs text-muted-foreground">by {selected.closed_by}</div>}
                  </div>
                )}
                {selected.status === "actioned" && (
                  <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
                    <div className="font-medium text-amber-600 dark:text-amber-400">Moved to an action</div>
                    <a href="/app/actions" className="mt-1 inline-block text-xs font-medium text-[hsl(var(--accent))]">View in Action board →</a>
                  </div>
                )}

                {/* Triage */}
                {canTriage && !done && mode === "view" && (
                  <div className="space-y-2 border-t border-border pt-4">
                    <div className="text-xs font-medium text-muted-foreground">Triage this report</div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setResolution("completed"); setMode("close"); }} data-testid="triage-close-btn">
                        <CheckCircle2 className="mr-2 h-4 w-4" /> Close
                      </Button>
                      <Button className="flex-1 rounded-full" onClick={startAction} data-testid="triage-action-btn">
                        <ArrowRightCircle className="mr-2 h-4 w-4" /> Move to Action
                      </Button>
                    </div>
                  </div>
                )}

                {/* Close flow */}
                {mode === "close" && (
                  <div className="space-y-3 border-t border-border pt-4">
                    <button onClick={() => setMode("view")} className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--accent))]"><ArrowLeft className="h-3 w-3" /> Back</button>
                    <div className="space-y-1.5">
                      <Label>Outcome</Label>
                      <Select value={resolution} onValueChange={setResolution}>
                        <SelectTrigger data-testid="close-resolution"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="no_action">No action required</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Note</Label>
                      <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a closing note…" data-testid="close-note" />
                    </div>
                    <Button disabled={busy} onClick={() => doClose(resolution)} className="w-full rounded-full" data-testid="close-confirm-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{resolution === "no_action" ? <XCircle className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Close Report</>}
                    </Button>
                  </div>
                )}

                {/* Move to Action flow */}
                {mode === "toAction" && (
                  <div className="space-y-3 border-t border-border pt-4">
                    <button onClick={() => setMode("view")} className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--accent))]"><ArrowLeft className="h-3 w-3" /> Back</button>
                    <div className="space-y-1.5"><Label>Title</Label><Input value={aForm.title} onChange={(e) => setAForm({ ...aForm, title: e.target.value })} data-testid="action-form-title" /></div>
                    <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} value={aForm.description} onChange={(e) => setAForm({ ...aForm, description: e.target.value })} data-testid="action-form-desc" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>Priority</Label>
                        <Select value={aForm.priority} onValueChange={(v) => setAForm({ ...aForm, priority: v })}>
                          <SelectTrigger data-testid="action-form-priority"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label>Current status</Label>
                        <Select value={aForm.status} onValueChange={(v) => setAForm({ ...aForm, status: v })}>
                          <SelectTrigger data-testid="action-form-status"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1.5"><Label>Due date</Label><Input type="date" value={aForm.due_date || ""} onChange={(e) => setAForm({ ...aForm, due_date: e.target.value })} data-testid="action-form-due" /></div>
                    <Button disabled={busy} onClick={submitAction} className="w-full rounded-full" data-testid="action-form-submit">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowRightCircle className="mr-2 h-4 w-4" /> Create Action</>}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Meta = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-medium">{value}</div>
  </div>
);
